import type { MarketplaceService, VariantPricing } from '../../types/pricing';

export interface QuantityAddonMetadata {
  config_key?: string;
  unit_label?: string;
  default_quantity?: number;
  max_quantity?: number;
  included_quantity?: number;
  price_cents?: number;
  frequency?: string;
  type?: string;
  allowed_variant_keys?: string[];
  variant_key?: string;
}

interface QuantityTierPricingRule {
  term_months?: number;
  min_quantity?: number;
  max_quantity?: number | null;
  recurring_per_unit_cents?: number;
  setup_per_unit_cents?: number;
  unit_label?: string;
  frequency?: string;
}

export interface AdditionalUserOffer {
  enabled: boolean;
  includedUsers: number;
  pricePerUserCents: number;
  unitLabel: string;
  maxQuantity: number | null;
  frequency: string;
  variant?: VariantPricing;
  pricingMode?: 'incremental' | 'absolute_tiered';
  totalRecurringCents?: number;
  totalOneTimeCents?: number;
  configuredQuantity?: number;
  tier?: QuantityTierPricingRule | null;
}

export interface ServiceConfigurationPayload {
  additional_users?: number;
}

function getPrimaryQuantityAddon(
  service: MarketplaceService,
  variantKey?: string,
): QuantityAddonMetadata | null {
  const quantityAddons = Array.isArray(service.metadata?.quantity_addons)
    ? service.metadata.quantity_addons
    : [];

  if (!quantityAddons.length) return null;

  const normalizedVariantKey = String(variantKey || '').trim();
  if (normalizedVariantKey) {
    const matched = quantityAddons.find((entry: QuantityAddonMetadata) => {
      const directVariantKey = String(entry?.variant_key || '').trim();
      const allowedVariantKeys = Array.isArray(entry?.allowed_variant_keys)
        ? entry.allowed_variant_keys.map((value) => String(value).trim()).filter(Boolean)
        : [];
      return (
        directVariantKey === normalizedVariantKey ||
        allowedVariantKeys.includes(normalizedVariantKey)
      );
    });
    if (matched) return matched;
  }

  return quantityAddons[0] || null;
}

function getQuantityTierRules(service: MarketplaceService): QuantityTierPricingRule[] {
  return Array.isArray(service.metadata?.quantity_tier_pricing)
    ? service.metadata.quantity_tier_pricing
    : [];
}

function getMatchingQuantityTier(
  service: MarketplaceService,
  quantity: number,
  selectedTerm?: number | null,
): QuantityTierPricingRule | null {
  const rules = getQuantityTierRules(service);
  if (!rules.length || quantity <= 0) return null;

  const termMatched = rules.filter((rule) => {
    if (selectedTerm == null) return true;
    return rule.term_months == null || Number(rule.term_months) === Number(selectedTerm);
  });

  const candidates = (termMatched.length ? termMatched : rules).filter((rule) => {
    const minQuantity = Math.max(1, Number(rule.min_quantity) || 1);
    const maxQuantity = rule.max_quantity == null ? Number.MAX_SAFE_INTEGER : Number(rule.max_quantity);
    return quantity >= minQuantity && quantity <= maxQuantity;
  });

  if (!candidates.length) return null;

  return candidates.sort((a, b) => {
    const aMin = Number(a.min_quantity) || 1;
    const bMin = Number(b.min_quantity) || 1;
    return bMin - aMin;
  })[0];
}

export function getSelectedVariant(service: MarketplaceService, variantKey?: string): VariantPricing | undefined {
  return service.variants?.variants?.find((variant) => variant.key === variantKey);
}

export function getIncludedUsersFromDescription(description?: string | null): number {
  if (!description) return 0;

  const overMatch = description.match(/over\s+(\d+)/i);
  if (overMatch) return Number(overMatch[1]) || 0;

  const upToMatch = description.match(/up to\s+(\d+)\s+users?/i);
  if (upToMatch) return Number(upToMatch[1]) || 0;

  const plusUsersMatch = description.match(/(\d+)\+\s+users?/i);
  if (plusUsersMatch) return Number(plusUsersMatch[1]) || 0;

  return 0;
}

export function getAdditionalUserOffer(
  service: MarketplaceService,
  variantKey?: string,
  selectedTerm?: number | null,
  configuredQuantity?: number,
): AdditionalUserOffer {
  const quantityAddon = getPrimaryQuantityAddon(service, variantKey);
  const variant = getSelectedVariant(service, variantKey);
  const maxQuantity = Number.isFinite(Number(quantityAddon?.max_quantity))
    ? Number(quantityAddon?.max_quantity)
    : null;
  const normalizedConfiguredQuantity = clampAdditionalUsers(
    configuredQuantity ?? Number(quantityAddon?.default_quantity ?? 0),
    maxQuantity,
  );
  const tier = getMatchingQuantityTier(service, normalizedConfiguredQuantity, selectedTerm);

  if (tier) {
    const unitLabel = String(tier.unit_label || quantityAddon?.unit_label || 'line').trim() || 'line';
    const pricePerUserCents = Math.max(0, Number(tier.recurring_per_unit_cents) || 0);
    const setupPerUnitCents = Math.max(0, Number(tier.setup_per_unit_cents) || 0);
    const frequency = String(tier.frequency || quantityAddon?.frequency || variant?.pricing?.frequency || 'monthly');

    return {
      enabled: Boolean(variant && pricePerUserCents > 0),
      includedUsers: 0,
      pricePerUserCents,
      unitLabel,
      maxQuantity,
      frequency,
      variant,
      pricingMode: 'absolute_tiered',
      totalRecurringCents: normalizedConfiguredQuantity * pricePerUserCents,
      totalOneTimeCents: normalizedConfiguredQuantity * setupPerUnitCents,
      configuredQuantity: normalizedConfiguredQuantity,
      tier,
    };
  }

  const includedUsers = Math.max(
    0,
    Number(quantityAddon?.included_quantity ?? getIncludedUsersFromDescription(variant?.description) ?? 0) || 0,
  );
  const pricePerUserCents = Math.max(
    0,
    Number(quantityAddon?.price_cents ?? service.metadata?.additional_user_price_cents ?? 0) || 0,
  );
  const unitLabel = String(quantityAddon?.unit_label || 'user').trim() || 'user';
  const frequency = String(quantityAddon?.frequency || variant?.pricing?.frequency || 'monthly');

  return {
    enabled: Boolean(variant && pricePerUserCents > 0),
    includedUsers,
    pricePerUserCents,
    unitLabel,
    maxQuantity,
    frequency,
    variant,
    pricingMode: 'incremental',
    totalRecurringCents: normalizedConfiguredQuantity * pricePerUserCents,
    totalOneTimeCents: 0,
    configuredQuantity: normalizedConfiguredQuantity,
    tier: null,
  };
}

export function clampAdditionalUsers(value: number, maxQuantity?: number | null): number {
  if (!Number.isFinite(value)) return 0;
  const normalized = Math.max(0, Math.floor(value));
  if (Number.isFinite(Number(maxQuantity)) && Number(maxQuantity) >= 0) {
    return Math.min(normalized, Number(maxQuantity));
  }
  return normalized;
}

export function getAdditionalUserTotalCents(
  service: MarketplaceService,
  variantKey?: string,
  additionalUsers: number = 0,
  selectedTerm?: number | null,
): number {
  const offer = getAdditionalUserOffer(service, variantKey, selectedTerm, additionalUsers);
  return offer.enabled ? Number(offer.totalRecurringCents || 0) : 0;
}

export function getAdditionalUserOneTimeTotalCents(
  service: MarketplaceService,
  variantKey?: string,
  additionalUsers: number = 0,
  selectedTerm?: number | null,
): number {
  const offer = getAdditionalUserOffer(service, variantKey, selectedTerm, additionalUsers);
  return offer.enabled ? Number(offer.totalOneTimeCents || 0) : 0;
}
